import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { createClient } from '@/lib/supabase/client'
import { DataTable, DataTableColumn } from '@/components/ui/data-table'
import { CrudForm, CrudFormField } from '@/components/forms/crud-form'
import { adminApi } from '@/lib/api-client'

export default function AlunosPage() {
  const supabase = createClient()
  const [alunos, setAlunos] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const alunoFields: CrudFormField[] = [
    { name: 'name', label: 'Nome', type: 'text', required: true },
    { name: 'phone', label: 'Telefone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'address', label: 'Endereço', type: 'text' },
  ];

  useEffect(() => {
    const fetchAlunos = async () => {
      setLoading(true);
      let query = supabase.from('users').select('*');
      if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
      }
      const { data } = await query;
      setAlunos(data || []);
      setLoading(false);
    };
    fetchAlunos();
  }, [search]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (editId) {
      // Edição
      await supabase.from('users').update(form).eq('id', editId);
    } else {
      // Criação via API segura
      try {
        await adminApi.inviteUser({
          email: form.email,
          name: form.name,
          phone: form.phone,
          address: form.address,
        });
      } catch (error: any) {
        alert('Erro ao criar usuário: ' + (error.message || ''));
        setLoading(false);
        return;
      }
    }
    setOpen(false);
    setForm({ name: '', phone: '', email: '', address: '' });
    setEditId(null);
    setLoading(false);
    // Recarregar lista
    let query = supabase.from('users').select('*');
    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
    }
    const { data } = await query;
    setAlunos(data || []);
  };

  const handleEdit = (aluno: any) => {
    setForm({
      name: aluno.name || '',
      phone: aluno.phone || '',
      email: aluno.email || '',
      address: aluno.address || '',
    });
    setEditId(aluno.id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ name: '', phone: '', email: '', address: '' });
  };

  const handleDelete = async (alunoId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este aluno?')) return;
    setLoading(true);
    await supabase.from('users').delete().eq('id', alunoId);
    setAlunos((prev) => prev.filter((c) => c.id !== alunoId));
    setLoading(false);
  };

  const alunoColumns: DataTableColumn<any>[] = [
    { key: 'name', label: 'Nome' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefone' },
    { key: 'address', label: 'Endereço' },
    { key: 'created_at', label: 'Criado em', render: c => c.created_at ? new Date(c.created_at).toLocaleDateString() : '-' },
    {
      key: 'actions',
      label: 'Ações',
      render: c => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}>Editar</Button>
          <Button variant="destructive" size="sm" onClick={() => handleDelete(c.id)} className="ml-2">Excluir</Button>
        </>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Alunos</h2>
          <p className="text-muted-foreground">
            Gerencie todos os usuários/alunos da plataforma
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Buscar alunos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-56"
          />
          <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : handleClose()}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto" onClick={() => { setEditId(null); setForm({ name: '', phone: '', email: '', address: '' }); }}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Aluno
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editId ? 'Editar Aluno' : 'Novo Aluno'}</DialogTitle>
              </DialogHeader>
              <CrudForm
                fields={alunoFields}
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={handleClose}
                submitLabel="Salvar"
                cancelLabel="Cancelar"
                loading={loading}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <DataTable
          columns={alunoColumns}
          data={alunos}
          loading={loading}
        />
      </div>
    </div>
  )
} 